<template>
  <div class="container py-24">
    <div class="mx-auto max-w-2xl">
      <!-- Page header -->
      <div class="mb-8">
        <h1 class="text-4xl font-bold text-foreground">{{ $t('cookieSettings.title') }}</h1>
        <p class="mt-2 text-lg text-muted-foreground">{{ $t('cookieSettings.description') }}</p>
      </div>

      <!-- Consent Preferences Card -->
      <Card class="mb-6">
        <CardHeader>
          <CardTitle>{{ $t('cookieSettings.preferencesTitle') }}</CardTitle>
          <CardDescription>{{ $t('cookieSettings.preferencesDescription') }}</CardDescription>
        </CardHeader>
        <CardContent class="space-y-4">
          <!-- Essential (always on) -->
          <div class="flex items-start justify-between gap-4 rounded-lg bg-muted/50 p-4">
            <div class="flex-1">
              <p class="text-sm font-medium">{{ $ts('consent.essential.label') }}</p>
              <p class="mt-1 text-xs text-muted-foreground">{{ $ts('consent.essential.description') }}</p>
            </div>
            <button
              type="button"
              disabled
              class="relative inline-flex h-6 w-11 shrink-0 cursor-not-allowed items-center rounded-full bg-primary opacity-70"
              :aria-label="$ts('consent.essential.label')"
            >
              <span class="pointer-events-none inline-block h-5 w-5 translate-x-5 rounded-full bg-white shadow-sm" />
            </button>
          </div>

          <!-- Analytics -->
          <div class="flex items-start justify-between gap-4 rounded-lg p-4 hover:bg-muted/30 transition-colors">
            <div class="flex-1">
              <p class="text-sm font-medium">{{ $ts('consent.analytics.label') }}</p>
              <p class="mt-1 text-xs text-muted-foreground">{{ $ts('consent.analytics.description') }}</p>
            </div>
            <button
              type="button"
              role="switch"
              :aria-checked="localPrefs.analytics"
              :aria-label="$ts('consent.analytics.label')"
              :class="[
                'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors',
                localPrefs.analytics ? 'bg-primary' : 'bg-input',
              ]"
              @click="localPrefs.analytics = !localPrefs.analytics"
            >
              <span
                :class="[
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                  localPrefs.analytics ? 'translate-x-5' : 'translate-x-0.5',
                ]"
              />
            </button>
          </div>

          <!-- Marketing -->
          <div class="flex items-start justify-between gap-4 rounded-lg p-4 hover:bg-muted/30 transition-colors">
            <div class="flex-1">
              <p class="text-sm font-medium">{{ $ts('consent.marketing.label') }}</p>
              <p class="mt-1 text-xs text-muted-foreground">{{ $ts('consent.marketing.description') }}</p>
            </div>
            <button
              type="button"
              role="switch"
              :aria-checked="localPrefs.marketing"
              :aria-label="$ts('consent.marketing.label')"
              :class="[
                'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors',
                localPrefs.marketing ? 'bg-primary' : 'bg-input',
              ]"
              @click="localPrefs.marketing = !localPrefs.marketing"
            >
              <span
                :class="[
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                  localPrefs.marketing ? 'translate-x-5' : 'translate-x-0.5',
                ]"
              />
            </button>
          </div>

          <!-- Third-party -->
          <div class="flex items-start justify-between gap-4 rounded-lg p-4 hover:bg-muted/30 transition-colors">
            <div class="flex-1">
              <p class="text-sm font-medium">{{ $ts('consent.thirdParty.label') }}</p>
              <p class="mt-1 text-xs text-muted-foreground">{{ $ts('consent.thirdParty.description') }}</p>
            </div>
            <button
              type="button"
              role="switch"
              :aria-checked="localPrefs.third_party"
              :aria-label="$ts('consent.thirdParty.label')"
              :class="[
                'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors',
                localPrefs.third_party ? 'bg-primary' : 'bg-input',
              ]"
              @click="localPrefs.third_party = !localPrefs.third_party"
            >
              <span
                :class="[
                  'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                  localPrefs.third_party ? 'translate-x-5' : 'translate-x-0.5',
                ]"
              />
            </button>
          </div>
        </CardContent>
        <CardFooter class="flex flex-col gap-3 sm:flex-row sm:justify-between">
          <div class="flex gap-2">
            <Button variant="outline" size="sm" @click="handleRejectAll">
              {{ $t('consent.rejectAll') }}
            </Button>
            <Button variant="outline" size="sm" @click="handleAcceptAll">
              {{ $t('consent.acceptAll') }}
            </Button>
          </div>
          <Button size="sm" @click="handleSave">
            {{ $t('consent.savePreferences') }}
          </Button>
        </CardFooter>
      </Card>

      <!-- Privacy Policy Link -->
      <div class="text-center">
        <p class="text-sm text-muted-foreground">
          {{ $t('cookieSettings.moreInfo') }}
          <NuxtLink
            :to="$localeRoute('/legal/privacy')"
            class="underline hover:text-foreground transition-colors"
          >
            {{ $t('consent.privacyLink') }}
          </NuxtLink>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue';
import { useI18n, useSeoMeta } from '#imports';
import { useConsentState, type ConsentPreferences } from '#bubba/shared/composables/consent/useConsentState';
import Card from '#bubba/shared/components/ui/card/Card.vue';
import CardContent from '#bubba/shared/components/ui/card/CardContent.vue';
import CardDescription from '#bubba/shared/components/ui/card/CardDescription.vue';
import CardFooter from '#bubba/shared/components/ui/card/CardFooter.vue';
import CardHeader from '#bubba/shared/components/ui/card/CardHeader.vue';
import CardTitle from '#bubba/shared/components/ui/card/CardTitle.vue';
import Button from '#bubba/shared/components/ui/button/Button.vue';

const { $t, $ts, $localeRoute } = useI18n();
const { state, savePreferences, acceptAll, rejectAll } = useConsentState();

useSeoMeta({
  title: () => $ts('cookieSettings.title'),
  description: () => $ts('cookieSettings.seo.description'),
});

const localPrefs = reactive<ConsentPreferences>({
  analytics: state.value.preferences.analytics,
  marketing: state.value.preferences.marketing,
  third_party: state.value.preferences.third_party,
});

function handleAcceptAll() {
  localPrefs.analytics = true;
  localPrefs.marketing = true;
  localPrefs.third_party = true;
  acceptAll();
}

function handleRejectAll() {
  localPrefs.analytics = false;
  localPrefs.marketing = false;
  localPrefs.third_party = false;
  rejectAll();
}

function handleSave() {
  savePreferences({ ...localPrefs });
}
</script>
